/**
 * OverflowScroller — a horizontal strip that may hold more than fits (a
 * drawer's tab list), with every item reachable and visibly so.
 *
 * - The strip is a named, keyboard-focusable region (arrow keys scroll it).
 * - A scroll button and an edge fade appear on a side ONLY while there is
 *   more content that way — never a fade over the first tab at rest.
 * - When `revealKey` changes (the active tab), the element matching
 *   `revealSelector` is scrolled into view, so a selected tab past the edge
 *   is never left half-hidden.
 *
 * Replaces the drawers' hand-rolled scrollers: a hidden scrollbar under two
 * permanent 24px fades, with no button and no focusable region (BW-11/35).
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '../lib/utils';

export interface OverflowScrollerProps {
  children: ReactNode;
  /** Names the scroll region for assistive technology. */
  label: string;
  /** Names the two scroll buttons. */
  scrollLabels: { previous: string; next: string };
  /** A selector for the element to keep in view (e.g. `[data-state="active"]`). */
  revealSelector?: string;
  /** When this changes, the `revealSelector` element is scrolled into view. */
  revealKey?: unknown;
  /** The outer frame (borders, background). */
  className?: string;
  /** The scrolling strip itself (padding). */
  contentClassName?: string;
}

/** How far one button press scrolls, as a share of the visible width. */
const PAGE_SHARE = 0.8;

export function OverflowScroller({
  children,
  label,
  scrollLabels,
  revealSelector,
  revealKey,
  className,
  contentClassName,
}: OverflowScrollerProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState({ previous: false, next: false });

  const measure = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const previous = strip.scrollLeft > 1;
    const next = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1;
    setMore((was) => (was.previous === previous && was.next === next ? was : { previous, next }));
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    measure();
    strip.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(strip);
    if (strip.firstElementChild) observer?.observe(strip.firstElementChild);
    return () => {
      strip.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    if (!revealSelector) return;
    const target = stripRef.current?.querySelector(revealSelector);
    if (target && 'scrollIntoView' in target) {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [revealSelector, revealKey]);

  const page = (direction: -1 | 1) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollBy({ left: direction * strip.clientWidth * PAGE_SHARE, behavior: 'smooth' });
  };

  const edgeButton = (side: 'previous' | 'next') => (
    <div
      className={cn(
        'pointer-events-none absolute inset-y-0 z-10 flex items-center',
        side === 'previous'
          ? 'left-0 bg-linear-to-r from-background via-background/90 to-transparent pr-4'
          : 'right-0 bg-linear-to-l from-background via-background/90 to-transparent pl-4'
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={scrollLabels[side]}
        onClick={() => page(side === 'previous' ? -1 : 1)}
        className="pointer-events-auto flex size-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        {side === 'previous' ? (
          <ChevronLeft className="size-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );

  return (
    <div className={cn('relative', className)}>
      {more.previous && edgeButton('previous')}
      <div
        ref={stripRef}
        role="region"
        aria-label={label}
        tabIndex={0}
        className={cn(
          'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
          contentClassName
        )}
      >
        {children}
      </div>
      {more.next && edgeButton('next')}
    </div>
  );
}
